// Hand-off step-up from the page's side (Erwin 2026-10-29 O1/O2/O3; Nexus Docs/DESIGN_WEB_HANDOFF_STEPUP_2026-10-29.md).
//
// A Helm XR web window (a hand-off session: amr `web_handoff`) cannot use a passkey — GeckoView has no FIDO2. When
// Helm asks for one (PASSKEY_REQUIRED), the window instead:
//   1. POST /auth/web-handoff/stepup (bearer = its own access token) -> {stepupCode, userCode, verificationUri, …};
//   2. hands {userCode, verificationUri, expiresIn, admin, headset} to the glasses' chooser (nx-handoff:stepup) — or,
//      with no glasses, shows the code and the link itself;
//   3. polls POST /auth/web-handoff/stepup/poll {stepupCode} every `interval` s until the code is approved with a
//      passkey on another device (O1/O3) or "Use this headset" is chosen (O2), then adopts the new access token
//      (the refresh token stays) and tells the glasses (nx-handoff:stepup-done).
// `stepupCode` is the poll handle — a secret only this page keeps; it is never logged or shown.

import type { AuthCaller, AuthReply } from './authCall';
import { codeOfReply } from './authCall';

export interface StepUpStart {
  stepupCode: string;
  userCode: string;
  verificationUri: string;
  expiresIn: number;
  interval: number;
  admin: boolean;
  headset: { eligible: boolean; reason?: string };
}

/** The 201 body of POST /auth/web-handoff/stepup, or null when it is not one. */
export function parseStepUpStart(b: Record<string, unknown>): StepUpStart | null {
  if (typeof b.stepupCode !== 'string' || !b.stepupCode) return null;
  if (typeof b.userCode !== 'string' || !b.userCode) return null;
  if (typeof b.verificationUri !== 'string' || !b.verificationUri) return null;
  const h = (b.headset && typeof b.headset === 'object' ? b.headset : {}) as Record<string, unknown>;
  return {
    stepupCode: b.stepupCode,
    userCode: b.userCode,
    verificationUri: b.verificationUri,
    expiresIn: typeof b.expiresIn === 'number' && b.expiresIn > 0 ? b.expiresIn : 300,
    interval: typeof b.interval === 'number' && b.interval >= 1 ? b.interval : 3,
    admin: b.admin === true,
    headset: { eligible: h.eligible === true, ...(typeof h.reason === 'string' ? { reason: h.reason } : {}) },
  };
}

export type StartResult = { ok: true; start: StepUpStart } | { ok: false; message: string };

export function startErrorMessage(r: AuthReply): string {
  switch (codeOfReply(r)) {
    case 'NOT_A_HANDOFF_SESSION': return 'This window was not opened from Helm XR — use your passkey instead.';
    default:
      if (r.status === 429) return 'Too many tries — wait a minute, then try again.';
      return typeof r.body.message === 'string' && r.body.message ? r.body.message : `Could not start the confirmation (HTTP ${r.status}).`;
  }
}

export async function startHandoffStepUp(call: AuthCaller): Promise<StartResult> {
  let r: AuthReply;
  try { r = await call('POST', 'web-handoff/stepup', {}); } catch { return { ok: false, message: 'Cannot reach the sign-in service right now — try again in a moment.' }; }
  if (r.status === 200 || r.status === 201) {
    const start = parseStepUpStart(r.body);
    if (start) return { ok: true, start };
  }
  return { ok: false, message: startErrorMessage(r) };
}

export type PollFinal =
  | { kind: 'ok'; accessToken: string }
  | { kind: 'denied' | 'expired' | 'used' | 'cancelled' | 'error'; message: string };
export type PollStep = { kind: 'pending'; slowDown?: boolean } | PollFinal;

export const STEPUP_MESSAGES = {
  denied: 'The confirmation was declined.',
  expired: 'The code expired before it was approved.',
  used: 'This code was already used.',
  cancelled: 'The confirmation was closed in the glasses.',
} as const;

/** One poll answer -> what the loop does next. */
export function classifyPoll(r: AuthReply): PollStep {
  const code = codeOfReply(r);
  if (r.status === 200) {
    return typeof r.body.accessToken === 'string' && r.body.accessToken
      ? { kind: 'ok', accessToken: r.body.accessToken }
      : { kind: 'error', message: 'The sign-in service sent an unexpected answer.' };
  }
  if (code === 'AUTHORIZATION_PENDING') return { kind: 'pending' };
  if (code === 'SLOW_DOWN' || r.status === 429) return { kind: 'pending', slowDown: true };   // back off, keep going
  if (code === 'ACCESS_DENIED') return { kind: 'denied', message: STEPUP_MESSAGES.denied };
  if (code === 'EXPIRED') return { kind: 'expired', message: STEPUP_MESSAGES.expired };
  if (code === 'ALREADY_USED') return { kind: 'used', message: STEPUP_MESSAGES.used };
  if (code === 'WRONG_SESSION') return { kind: 'error', message: 'This code belongs to another window.' };
  if (r.status >= 500) return { kind: 'pending' };   // server trouble: keep trying until the code expires
  return { kind: 'error', message: typeof r.body.message === 'string' && r.body.message ? r.body.message : `Could not confirm (HTTP ${r.status}).` };
}

export interface PollDeps {
  poll: () => Promise<AuthReply>;
  /** Waits `ms`, or less when the signal aborts. */
  sleep: (ms: number, signal: AbortSignal) => Promise<void>;
  signal: AbortSignal;
  now?: () => number;
  /**
   * L2: on ALREADY_USED, refresh the session and return the new access token when it carries the step-up
   * (amr mfa / handoff_headset) — an earlier poll consumed the approval and its answer was lost. Null = really used.
   */
  recover?: () => Promise<string | null>;
}

export const abortableSleep = (ms: number, signal: AbortSignal): Promise<void> => new Promise((resolve) => {
  if (signal.aborted) { resolve(); return; }
  const t = setTimeout(done, ms);
  function done() { clearTimeout(t); signal.removeEventListener('abort', done); resolve(); }
  signal.addEventListener('abort', done);
});

/**
 * Poll until the step-up is approved, refused, expired or cancelled (signal aborted). Network errors and 5xx keep
 * polling; the loop gives up on its own a little after `expiresIn`.
 */
export async function pollStepUp(start: Pick<StepUpStart, 'interval' | 'expiresIn'>, deps: PollDeps): Promise<PollFinal> {
  const now = deps.now ?? Date.now;
  const deadline = now() + (start.expiresIn + 10) * 1000;
  let interval = start.interval;
  const cancelled = (): PollFinal => ({ kind: 'cancelled', message: STEPUP_MESSAGES.cancelled });
  for (;;) {
    await deps.sleep(interval * 1000, deps.signal);
    if (deps.signal.aborted) return cancelled();
    if (now() > deadline) return { kind: 'expired', message: STEPUP_MESSAGES.expired };
    let step: PollStep;
    try { step = classifyPoll(await deps.poll()); } catch { step = { kind: 'pending' }; }
    if (step.kind === 'ok') return step;   // an approval that arrived is kept even if the chooser was just closed
    if (deps.signal.aborted) return cancelled();
    if (step.kind === 'pending') { if (step.slowDown) interval = Math.min(interval + 5, 30); continue; }
    if (step.kind === 'used' && deps.recover) {
      let token: string | null = null;
      try { token = await deps.recover(); } catch { token = null; }
      if (token) return { kind: 'ok', accessToken: token };
    }
    return step;
  }
}

export const pollRequest = (call: AuthCaller, stepupCode: string) => (): Promise<AuthReply> =>
  call('POST', 'web-handoff/stepup/poll', { stepupCode });
